import { ConfigSettingsService } from '../config-settings';
import { FeatureTile, MenuTile, MenuTileSubfeature } from '../../models';
import { Injectable, inject } from '@angular/core';
import { NavigationLinkRegistryService } from '../../nav';
import { marker as _ } from '@colsen1991/ngx-translate-extract-marker';

@Injectable()
export class TileMenuService {
  private readonly configService: ConfigSettingsService = inject(ConfigSettingsService);
  private readonly navigationLinkRegistry: NavigationLinkRegistryService = inject(NavigationLinkRegistryService);

  public async getTilesByRows(minColCount?: number): Promise<Array<Array<MenuTile>>> {
    return this.asyncIteratorToArray<MenuTile[]>(this.getTilesByRowsAsync(minColCount));
  }

  /**
   * Groups menu tiles by row.
   */
  public async *getTilesByRowsAsync(minColCount?: number): AsyncIterableIterator<MenuTile[]> {
    const tiles = this.configService.appSettings.dashboard?.menuTiles;
    if (!tiles) {
      return;
    }

    const hasTwoColumns = tiles.length > 2 && tiles.length % 2 === 0;
    let step = hasTwoColumns ? 2 : 1;
    step = minColCount && minColCount > step ? minColCount : step;
    const rows = tiles.length / step;

    for (let row = 0; row < rows; row++) {
      yield Array.from(await this.asyncIteratorToArray(this.getTilesInRow(tiles.slice(row * step, (row + 1) * step))));
    }
  }

  /**
   * Determines if the tiles should be displayed in a single column.
   * @returns true if the tiles should be displayed in a single column, otherwise false
   */
  public isSingleColumn(): boolean {
    const tilesLength = this.configService.appSettings.dashboard?.menuTiles?.length
      ? this.configService.appSettings.dashboard.menuTiles.length
      : 0;
    return tilesLength <= 3 || tilesLength % 2 !== 0;
  }

  /** Resolves submenu tiles that are specified by subfeature. */
  public resolveSubfeature(subfeature: string): MenuTileSubfeature | undefined {
    switch (subfeature) {
      case 'UNFINISHED_ANTRAGE_COUNT':
        return {
          class: 'badge pending-antraege',
          type: subfeature,
          label: _('dashboard.mark.unfinished_antrage_count'),
          aria: { label: _('a11y.mark.unfinished_antrage_count') },
        };
      case 'FINISHED_ANTRAGE_COUNT':
        return {
          class: 'badge completed-antraege',
          type: subfeature,
          label: _('dashboard.mark.finished_antrage_count'),
          aria: { label: _('a11y.mark.finished_antrage_count') },
        };
      case 'UNREAD_BESCHEIDE':
        return {
          class: 'badge unread-bescheide',
          type: subfeature,
          label: _('dashboard.mark.unread_bescheide_count'),
          aria: { label: _('a11y.mark.unread_bescheide_count') },
        };
      case 'TOTAL_COUNT_BESCHEIDE':
        return {
          class: 'badge total-bescheide',
          type: subfeature,
          label: _('dashboard.mark.total_bescheide_count'),
          aria: { label: _('a11y.mark.total_bescheide_count') },
        };
      default:
        return undefined;
    }
  }

  private async asyncIteratorToArray<T>(iterator: AsyncIterableIterator<T>): Promise<T[]> {
    const items = [];
    for await (const item of iterator) {
      items.push(item);
    }

    return items;
  }

  /**
   * Resolves feature tiles before returning the actual menu tile. Menu tiles are passed through untouched.
   * @param tiles tiles in one row
   */
  private async *getTilesInRow(tiles: Array<FeatureTile>): AsyncIterableIterator<MenuTile> {
    for await (const tile of tiles) {
      const navigationLink: MenuTile = await this.navigationLinkRegistry.mapSingleTileToEntry(tile.feature);
      if (!navigationLink) {
        continue;
      }

      if (navigationLink.feature === 'CUSTOM_NAVLINK') {
        navigationLink.link = tile.link;
        navigationLink.i18n = tile.label || '';
        navigationLink.itemType = 'link';
      }

      if (typeof navigationLink.route === 'string' && !navigationLink.route.startsWith('/')) {
        navigationLink.route = '/' + navigationLink.route;
      }

      yield {
        ...navigationLink,
        subfeaturesmenu: tile.subfeature
          ? tile.subfeature
              .map((subfeature) => this.resolveSubfeature(subfeature))
              .filter((subfeature): subfeature is MenuTileSubfeature => subfeature !== undefined)
          : [],
      };
    }
  }
}
